'use client'

import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { useContentFamiliarity } from '@/hooks/useContentFamiliarity'
import { fitLabel, type ContentFit } from '@/lib/familiarity-core'
import { cn } from '@/lib/utils'
import type { ReaderContent } from '@/types/reader'

function percent(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.round(Math.max(0, Math.min(1, value)) * 100)
}

function fitVariant(fit: ContentFit) {
  if (fit === 'review') return 'success'
  if (fit === 'good-fit') return 'secondary'
  return 'outline'
}

function Metric({
  label,
  value,
  detail,
  progress
}: {
  label: string
  value: string
  detail: string
  progress: number
}) {
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground text-xs font-medium">
          {label}
        </span>
        <span className="text-sm font-semibold">{value}</span>
      </div>
      <Progress value={progress} size="sm" />
      <p className="text-muted-foreground truncate text-xs">{detail}</p>
    </div>
  )
}

export function ContentFamiliarityStrip({
  content,
  className
}: {
  content: ReaderContent
  className?: string
}) {
  const { data: summary, isLoading } = useContentFamiliarity(content)

  if (!summary && !isLoading) return null
  const progressCopy =
    content.sourceType === 'story'
      ? 'Story progress appears after reading'
      : 'Progress appears after reading'

  return (
    <section
      className={cn('border-border/70 mb-5 border-y py-4 sm:mb-7', className)}
      aria-label="Content familiarity"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Familiarity</h2>
          <p className="text-muted-foreground mt-1 hidden text-xs sm:block">
            Based on saved study notes, reviewed cards, and progress.
          </p>
        </div>
        {summary ? (
          <Badge variant={fitVariant(summary.fit)}>
            {fitLabel(summary.fit)}
          </Badge>
        ) : (
          <Badge variant="outline">Loading</Badge>
        )}
      </div>

      {summary ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            label="Words seen"
            value={
              summary.totalWordCount > 0
                ? `${summary.seenWordCount}/${summary.totalWordCount}`
                : 'No linked words'
            }
            detail={
              summary.totalWordCount > 0
                ? `${summary.savedWordCount} saved · ${summary.newWordCount} new`
                : 'Word coverage appears when entries are linked'
            }
            progress={percent(summary.seenWordRatio)}
          />
          <Metric
            label="Characters"
            value={
              summary.totalCharacterCount > 0
                ? `${summary.seenCharacterCount}/${summary.totalCharacterCount}`
                : 'No characters'
            }
            detail={
              summary.totalCharacterCount > 0
                ? `${summary.newCharacterCount} new`
                : 'Character coverage appears while reading'
            }
            progress={percent(summary.characterCoverageRatio)}
          />
          <Metric
            label="Grammar seen"
            value={
              summary.totalGrammarCount > 0
                ? `${summary.seenGrammarCount}/${summary.totalGrammarCount}`
                : 'No tagged grammar'
            }
            detail={
              summary.totalGrammarCount > 0
                ? `${summary.studiedGrammarCount} studying · ${summary.newGrammarCount} new`
                : 'Grammar coverage appears when concepts are tagged'
            }
            progress={percent(summary.grammarCoverageRatio)}
          />
          <Metric
            label="Progress"
            value={
              summary.progressRatio === null
                ? 'Not started'
                : `${percent(summary.progressRatio)}%`
            }
            detail={
              summary.progressRatio === null
                ? progressCopy
                : 'Started from your reader position'
            }
            progress={percent(summary.progressRatio)}
          />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {['Words', 'Characters', 'Grammar', 'Progress'].map((label) => (
            <div key={label} className="space-y-2">
              <div className="bg-muted h-4 w-24 rounded" />
              <div className="bg-muted h-1 rounded-full" />
              <div className="bg-muted h-3 w-32 rounded" />
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
